import { Box, Menu, TextField } from "@mui/material";
import { type ChangeEventHandler, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { isOtherReasonLabel } from "../../utils/callIns";
import { useCallInReasonOptions } from "../team/CallInReasons/callInReasonsHooks";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown/SystemDropdown";
import { InlineHint } from "../UIToolkit/molecules/InlineHint";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";

/** Option id for the appended stored value — it has no `department_call_in_reason` row to borrow one from. */
const STORED_VALUE_OPTION_ID = "stored-reason";

interface NurseReasonMenuProps {
	handleMenuClose: (isSubmit: boolean) => void;
	anchorEl: HTMLElement | null;
	menuOpen: boolean;
	/** The reason currently shown in the picker — the literal label persisted to `nurse_in_shift.message`. */
	editMessage: string;
	handleEditMessageChange: (value: string) => void;
	setEditMessage: (value: string) => void;
	customMessage: string;
	isLoading?: boolean;
}

export const NurseReasonMenu = ({
	handleMenuClose,
	anchorEl,
	menuOpen,
	editMessage,
	handleEditMessageChange,
	customMessage,
	setEditMessage,
	isLoading,
}: NurseReasonMenuProps) => {
	const { t } = useTranslation();
	const { isSnf, isHospital } = useHospitalData();
	// Hospitals render the free-text box below instead of a picker, so there is nothing to fetch for them.
	const { options, isLoading: isLoadingReasons, isError: isReasonsError } = useCallInReasonOptions({ enabled: !isHospital });

	/**
	 * The department's options, plus the current value when it isn't one of them.
	 *
	 * Two cases need that, and both are the same fix: a **historical** value (a since-renamed or archived
	 * reason, "Child Care", legacy free text) that must stay readable rather than be relabelled, and the
	 * moment before the query resolves, when `options` is empty and the trigger would otherwise render blank
	 * where the row's reason should be.
	 *
	 * Deliberately a one-way door: the entry is keyed off the CURRENT value, so picking a configured reason
	 * drops it from the list. A retired label may be preserved, never newly assigned — reopen the row to get
	 * it back.
	 */
	const reasonItems = useMemo(() => {
		if (!editMessage || options.some((option) => option.value === editMessage)) return options;
		return [...options, { value: editMessage, label: editMessage, id: STORED_VALUE_OPTION_ID }];
	}, [options, editMessage]);

	const isOtherSelected = isOtherReasonLabel(editMessage);

	const handleChange = useCallback<ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
		(event) => {
			const { value } = event.target;

			handleEditMessageChange(value);
			setEditMessage(value);
		},
		[handleEditMessageChange, setEditMessage],
	);

	return (
		<Menu
			onClose={() => handleMenuClose(false)}
			anchorEl={anchorEl}
			open={menuOpen}
			transitionDuration={0}
			sx={{
				"& .MuiMenu-paper": { minWidth: "320px" },
				// The panel is a form, not a list of menu items: drop the list's own vertical padding so the
				// content sits in one even 12px frame instead of 20px top/bottom against 12px at the sides.
				"& .MuiList-root": { py: 0 },
			}}
			slotProps={{
				paper: {
					sx: { overflow: "visible", mt: 1 },
				},
			}}
			anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			transformOrigin={{ vertical: "top", horizontal: "right" }}
		>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1.5 }}>
				{isHospital ? (
					<TextField multiline fullWidth autoFocus variant="standard" value={editMessage} onChange={handleChange} onClick={(e) => e.stopPropagation()} />
				) : (
					<>
						<SystemDropdown
							items={reasonItems}
							selectedItem={editMessage}
							onChange={(value) => {
								if (value !== undefined) setEditMessage(value);
							}}
							fullWidth
							// The picker opens on a row click, so the reasons can still be in flight. Hold it disabled
							// rather than offering a list of one — `reasonItems` keeps the row's own reason on screen.
							disabled={isLoadingReasons}
							loading={isLoadingReasons}
							label={t("reason")}
							data-testid="nurse-reason-menu-absence-type"
						/>
						{isOtherSelected && (
							<TextField
								multiline
								fullWidth
								autoFocus
								variant="standard"
								// Controlled by the caller's value, with no local mirror: the menu stays mounted when the
								// user clicks a different row, so a `useState` seeded once would keep the previous row's
								// text. The value is trimmed where it is saved, not per keystroke.
								value={customMessage}
								onChange={(e) => handleEditMessageChange(e.target.value)}
								onKeyDown={(e) => e.stopPropagation()}
								placeholder={t("enter.reason")}
								data-testid="nurse-reason-menu-other-reason"
							/>
						)}
						{/* A failed read leaves `options` empty, so the picker would show only the row's own reason and
						    read as "this department has one reason". Say what actually happened instead. */}
						<InlineHint open={isReasonsError} tone="warning" dataTestId="nurse-reason-menu-reasons-error-hint">
							{t("error.general")}
						</InlineHint>
					</>
				)}

				<SystemButton
					// No reason selected means we have nothing to persist — never guess one on the nurse's behalf.
					// Held shut while the list is unresolved too: the value would be the row's stored message
					// rather than its configured label, so the same click would persist different strings.
					// The error case is gated on having NO list, not on the error flag — a refetch that fails
					// leaves the last good list in place, and blocking a usable picker over a background blip
					// would be the worse failure.
					disabled={!editMessage.trim() || (isOtherSelected && !customMessage.trim()) || isLoadingReasons || (isReasonsError && options.length === 0)}
					buttonVariant={"primary"}
					loading={isLoading}
					onClick={() => handleMenuClose(true)}
					data-testid="nurse-reason-menu-save"
					sx={{ height: "30px" }}
				>
					{isSnf ? t("save.reason") : t("save.message")}
				</SystemButton>
			</Box>
		</Menu>
	);
};
